import { Modal, Box } from "@mui/material";
import { IoMdClose } from "react-icons/io";
import APISearchInput from "../../../search/APISearchInput";
import CustomPagination from "../../../table/CustomPagination";
import CustomStyledTable from "../../../table/CustomStyledTable";
import TableFilter from "../../../table/filter/TableFilter";
import { TableFilterField } from "../../../table/filter/model";
import { ModalStyle } from "../../../../helpers/appHelpers";
import { useManageTableWithStateVariables } from "../../../../helpers/useManageTable";
import { PERMISSIONS, useCheckUserPermissions } from "../../../../helpers/usePermissionManager";
import { AuditLogDto, AuditLogOrderBy } from "../../../../models/audit-log.dto";
import { useGetAuditLogSearch, useGetAuditLog } from "../../../../services/audit-log.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useUserStore from "../../../../state-management/useUserStore";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { AuditLogColumns, AuditLogOutcomeOptions } from "./data";
import AuditLogDetailsModal from "./AuditLogDetailsModal";

type OrgAuditLogsProps = {
	openModal: boolean;
	setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrgAuditLogs = ({
	openModal,
	setOpenModal,
}: OrgAuditLogsProps) => {
	const { currentUser, userType } = useUserStore();
    const { savedCompanies } = useCompanyStore();
	const [tableKey, setTableKey] = useState(Math.random());
	const [companyName, setCompanyName] = useState("");
	const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLogDto | null>(null);
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	// The audit service only returns logs of payroll-related services to these users
	const canViewPayrollLogs = checkUserPermissionsAnd([PERMISSIONS.PAYROLL_WRITE]);

	// Falls back to the company in the response for companies not loaded here
	const getCompanyName = (
		companyId: number | null | undefined,
		company?: AuditLogDto["company"],
	) =>
		savedCompanies.find((saved) => saved.id === companyId)?.name
			|| company?.name
			|| "";
	
	const { mutate: searchAuditLogs } = useGetAuditLogSearch();
	const {
        tableAPIRef,
        defaultTableData,
        tableData,
		tableFilter,
        isLoading,
        page,
        pageCount,
		setTableData,
		setTableFilter,
		setSearching,
        handlePageChange,
	} = useManageTableWithStateVariables<AuditLogDto>({ 
		fetchHook: useGetAuditLog,
		options: { 
			organizationId: currentUser.user!.organizationId,
			orderBy: AuditLogOrderBy.TIMESTAMP_DESC,
		}
	});

	const columnsWithCompany: GridColDef[] = [
		{
			field: "companyId",
			headerName: "Company",
			minWidth: 200,
			flex: 1,
			headerClassName: "super-app-theme--header",
			valueGetter: (params) => getCompanyName(params.value, params.row.company) || "--",
		},
		...AuditLogColumns
	]
	const [tableColumns, setTableColumns] = useState<GridColDef[]>(
		userType === "ORGANIZATION" ? columnsWithCompany : AuditLogColumns
	);

	useEffect(() => {
		if (userType === "ADMIN") return;

		if (tableFilter?.companyId) {
			setTableColumns(AuditLogColumns);
			setTableKey(Math.random());
			setCompanyName(getCompanyName(Number(tableFilter.companyId)));
			return
		}

		setCompanyName("");
		setTableColumns(columnsWithCompany);
	}, [tableFilter])

	const filterFields: TableFilterField[] = [
		{
			title: "",
			fieldName: "",
			fieldType: "SORT",
			options: [
				{
					title: "Timestamp",
					fieldName: "timestamp",
				},
			]
		},
		{
			title: "Date",
			fieldName: "timestamp",
			fieldType: "DATE_RANGE",
		},
		{
			title: "Outcome",
			fieldName: "outcome",
			fieldType: "SELECT_FIELD",
			options: AuditLogOutcomeOptions,
		},
		{
			title: "Service",
			fieldName: "service",
			fieldType: "INPUT_FIELD",
			attributes: { placeholder: "e.g. organization" },
		},
	];

	return (
		<Modal
			open={openModal}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Box sx={ModalStyle} width={1260} fontSize={"13px"}>
				<div className="flex justify-between items-center pt-4 pb-6">
					<div className="flex items-center gap-5">
						<h1 className="font-bold text-lg">Audit Logs</h1>
						<div className="flex items-center gap-3">
							{/* The search endpoint only takes `q`, so it ignores the
							filters; searching also clears them */}
							<APISearchInput 
								fallbackData={defaultTableData}
								setTableData={setTableData}
								mutate={searchAuditLogs}
								setSearching={setSearching}
								attributes={{
									title: "Search matches actions and ignores the filters",
								}}
								popupTable
							/>
							<TableFilter
								fields={
									userType === "ORGANIZATION" 
										? [
											...filterFields,
											{
												title: "Company",
												fieldName: "companyId",
												fieldType: "SELECT_FIELD",
												options: savedCompanies,
											},
										]
										: filterFields
								}
								externalFilter={tableFilter}
								setExternalFilter={setTableFilter}
								setSearching={setSearching}
							/>
							{companyName && (
								<div className="bg-[#6978F0] font-semibold text-white whitespace-nowrap 
									text-xs px-2 py-[6px] rounded flex items-center justify-center"
								>
									{companyName}
								</div>
							)}
						</div>
					</div>
					<button onClick={() => setOpenModal(false)}>
						<IoMdClose className="text-xl hover:scale-[1.1]" />
					</button>
				</div>
				<hr className={canViewPayrollLogs ? "mb-6" : "mb-4"} />
				{!canViewPayrollLogs && (
					<p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-4 py-2 mb-4">
						Logs of payroll-related services are only shown to users with
						the payroll:write permission.
					</p>
				)}
				<CustomStyledTable
					key={tableKey}
					rows={tableData}
					columns={tableColumns}
					apiRef={tableAPIRef}
					loading={isLoading}
					pageCount={pageCount}
					onRowClick={(row: AuditLogDto) => setSelectedAuditLog(row)}
					pagination={
						<CustomPagination 
							pageCount={pageCount} 
							pageState={page}
							handleChange={handlePageChange}
						/>
					}
				/>
				{selectedAuditLog && (
					<AuditLogDetailsModal
						open={Boolean(selectedAuditLog)}
						setOpen={() => setSelectedAuditLog(null)}
						auditLog={selectedAuditLog}
						companyName={getCompanyName(
							selectedAuditLog.companyId, selectedAuditLog.company
						)}
					/>
				)}
			</Box>
		</Modal>
	);
};
 
export default OrgAuditLogs;